import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { io } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { UtilsService } from './utils.service';

/**
 * Native RunnerPrintRelay plugin contract. The implementation lives in
 * DineNGo-Android-Runner/native/print-relay/RunnerPrintRelay.kt and is only
 * present inside the DineNGo Android Runner shell (the Capacitor APK on the Elo
 * POS). In a plain browser the plugin proxy still exists but printRaw() is
 * unavailable and throws, so every caller here is guarded by isSupported().
 */
interface RunnerPrintRelayPlugin {
  printRaw(options: { host: string; port?: number; dataBase64: string }): Promise<{ success: boolean; bytesWritten: number }>;
}

const RunnerPrintRelay = registerPlugin<RunnerPrintRelayPlugin>('RunnerPrintRelay');

/**
 * Native UsbPrintRelay plugin contract (wired sibling of RunnerPrintRelay).
 * Implementation: DineNGo-Android-Runner/native/usb-print/UsbPrintRelay.kt.
 * Present only inside the Android Runner shell; guarded by usbSupported().
 */
interface UsbPrintRelayPlugin {
  listDevices(): Promise<{ devices: Array<{ vendorId: number; productId: number; serialNumber?: string }> }>;
  printRaw(options: {
    vendorId: number; productId: number; dataBase64: string;
    mode?: 'usb' | 'serial'; baud?: number; serial?: string;
  }): Promise<{ success: boolean; bytesWritten: number }>;
}

const UsbPrintRelay = registerPlugin<UsbPrintRelayPlugin>('UsbPrintRelay');

/** Shape of the job the backend fans out on the `android_runner_print` pool. */
interface RunnerPrintJob {
  type?: string;
  payload?: {
    deviceIP?: string;
    port?: number;
    labelBase64?: string;
    jobId?: string;
    /** 'ip' (default) = TCP:9100; 'usb'/'serial' = wired, local to a Runner. */
    connectionType?: 'ip' | 'usb' | 'serial';
    usbVendorId?: number;
    usbProductId?: number;
    usbSerial?: string;
    serialBaud?: number;
  };
}

/**
 * RunnerPrintRelayService — frontend counterpart to the backend Android Runner
 * print rung (socketManager.sendPrintJobToMerchantAndroidRunner) and the native
 * RunnerPrintRelay plugin.
 *
 * On the DineNGo Android Runner it opens a dedicated socket, registers into the
 * server's isolated `android_runner_print` pool, and listens for 'job' events.
 * When one arrives it decodes the byte-safe labelBase64 receipt/kitchen bytes
 * and hands them to the native plugin, which writes them to the LAN printer on
 * TCP :9100 — the LAST-RESORT rung fired only when neither a Clover station
 * socket nor a Pi helper accepted the job.
 *
 * Safety posture:
 *  - Browser-safe: isSupported() is false off-device, so start() no-ops and the
 *    device never registers into the runner pool. Nothing here touches the
 *    online Clover/Pi print flows.
 *  - Device-flag gated: an MDM-pushable Preferences kill switch
 *    (`runner_print_relay_enabled` = 'false') disables the relay without a
 *    rebuild. Defaults ON for the native Runner (its whole reason to exist).
 *  - jobId dedup: the server may redeliver a job across socket reconnects. The
 *    same sha256 jobId travels with it, so a job already printed on this device
 *    is dropped — matching the server-side ACK-no-retry / no-double-print
 *    discipline. A failed print is NOT recorded, so a genuine redelivery can
 *    still land.
 *  - No card data ever crosses this path — receipt/kitchen ESC/POS bytes only.
 */
@Injectable({ providedIn: 'root' })
export class RunnerPrintRelayService {
  /** MDM-pushable kill switch. Relay is ON unless this is the string 'false'. */
  static readonly ENABLED_PREF_KEY = 'runner_print_relay_enabled';
  /** Preferences key holding the bounded rolling set of printed jobIds. */
  static readonly PRINTED_PREF_KEY = 'runner_print_relay.printed_job_ids';
  /** Cap on the persisted printed-jobId ring so it can never grow unbounded. */
  static readonly PRINTED_MAX = 200;

  private socket: any;
  private merchantId: string | null = null;
  private started = false;

  // In-flight guard: a jobId currently being printed. Prevents a duplicate job
  // arriving mid-print from firing a second socket write before the first has
  // resolved and been recorded as printed.
  private readonly inFlight = new Set<string>();
  // Lazily-loaded mirror of the persisted printed-jobId ring (most-recent last).
  private printedCache: string[] | null = null;

  constructor(private utilsService: UtilsService) {}

  /** True only inside the native Android Runner where the plugin is available. */
  isSupported(): boolean {
    try {
      return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('RunnerPrintRelay');
    } catch {
      return false;
    }
  }

  /** True only inside the native Runner where the USB print plugin is present. */
  usbSupported(): boolean {
    try {
      return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('UsbPrintRelay');
    } catch {
      return false;
    }
  }

  /** Device-flag gate: supported hardware AND not disabled by MDM config. */
  async isEnabled(): Promise<boolean> {
    if (!this.isSupported()) { return false; }
    try {
      const pref = await Preferences.get({ key: RunnerPrintRelayService.ENABLED_PREF_KEY });
      return pref?.value !== 'false';
    } catch {
      // Absence of the preference means "not explicitly disabled" → enabled.
      return true;
    }
  }

  /**
   * Boot the relay for the signed-in Runner. Idempotent and browser-safe: no-ops
   * off-device, when disabled by MDM, or when no merchant is known yet. Safe to
   * call unconditionally from a native boot / app-init seam.
   */
  async start(): Promise<void> {
    if (this.started) { return; }
    if (!(await this.isEnabled())) { return; }

    const user = this.utilsService.getCurrentUser();
    this.merchantId = user?.merchantId || null;
    if (!this.merchantId) { return; }

    this.started = true;
    this.openSocket();
  }

  stop(): void {
    try { this.socket?.close(); } catch { /* best-effort */ }
    this.socket = null;
    this.started = false;
  }

  private openSocket(): void {
    // Same retry-forever posture as the other cashier-side connectors; a dropped
    // link self-heals and we re-register on every (re)connect so the device
    // stays in the runner pool.
    this.socket = io(`${environment.apiServerUrl}`, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => this.registerDevice());
    this.socket.on('reconnect', () => this.registerDevice());
    this.socket.on('job', (job: RunnerPrintJob) => { this.handleJob(job); });
  }

  private registerDevice(): void {
    if (!this.socket || !this.merchantId) { return; }
    // No deviceSerial → the server routes this into the isolated
    // androidRunnerDevicesByMerchant pool, invisible to every Pi/Clover sender.
    // Advertise usb_print too when the wired plugin is present, purely for
    // server-side observability — routing to wired printers is by broadcast +
    // per-Runner device ownership, not by capability lookup.
    const capabilities = ['android_runner_print'];
    if (this.usbSupported()) { capabilities.push('usb_print'); }
    this.socket.emit('registerDevice', {
      merchantId: this.merchantId,
      capabilities,
    });
  }

  /**
   * Handle one 'job' event: validate, dedup on jobId, decode, print. Never
   * throws — a bad job is logged-and-dropped, a print failure is left un-recorded
   * so a legitimate redelivery can still land. Returns the outcome for tests.
   */
  async handleJob(job: RunnerPrintJob): Promise<'printed' | 'duplicate' | 'skipped' | 'failed'> {
    const payload = job?.payload;
    const dataBase64 = payload?.labelBase64;
    const jobId = payload?.jobId;
    const connectionType = payload?.connectionType || 'ip';

    // A job with no printable bytes or no id is not actionable.
    if (!dataBase64 || !jobId) { return 'skipped'; }

    // Wired printers are local to a specific Runner. The backend broadcasts the
    // job to every Runner in the pool; only the one with the device attached
    // prints. Ownership is checked BEFORE dedup so a Runner that does not have
    // the printer never consumes the jobId — the owning Runner still prints.
    if (connectionType === 'usb' || connectionType === 'serial') {
      return this.handleWiredJob(payload, jobId, dataBase64, connectionType);
    }

    // Networked (TCP:9100) path — unchanged.
    const host = payload?.deviceIP?.trim();
    const port = payload?.port || 9100;
    if (!host) { return 'skipped'; }

    // Dedup: already printed on this device, or a copy is mid-print right now.
    if (this.inFlight.has(jobId) || await this.hasPrinted(jobId)) { return 'duplicate'; }

    this.inFlight.add(jobId);
    try {
      await this.printViaNative(host, port, dataBase64);
      // Record only on confirmed success. A failure below is intentionally NOT
      // recorded so a genuine server redelivery of the same jobId can retry.
      await this.recordPrinted(jobId);
      return 'printed';
    } catch {
      return 'failed';
    } finally {
      this.inFlight.delete(jobId);
    }
  }

  /**
   * Handle a wired (USB / USB-serial) job. Returns 'skipped' when this Runner
   * lacks the USB plugin or does not have the target device attached — WITHOUT
   * recording the jobId, so the Runner that owns the printer still prints it.
   * The owning Runner applies the same jobId dedup + record-on-success rule as
   * the TCP path.
   */
  private async handleWiredJob(
    payload: NonNullable<RunnerPrintJob['payload']>,
    jobId: string,
    dataBase64: string,
    mode: 'usb' | 'serial',
  ): Promise<'printed' | 'duplicate' | 'skipped' | 'failed'> {
    if (!this.usbSupported()) { return 'skipped'; }
    const vendorId = payload.usbVendorId;
    const productId = payload.usbProductId;
    if (vendorId == null || productId == null) { return 'skipped'; }

    // Only the Runner that physically has this device may print it.
    let owns = false;
    try { owns = await this.usbHasDevice(vendorId, productId, payload.usbSerial); } catch { owns = false; }
    if (!owns) { return 'skipped'; }

    if (this.inFlight.has(jobId) || await this.hasPrinted(jobId)) { return 'duplicate'; }

    this.inFlight.add(jobId);
    try {
      await this.printViaUsb(vendorId, productId, dataBase64, mode, payload.serialBaud, payload.usbSerial);
      await this.recordPrinted(jobId);
      return 'printed';
    } catch {
      return 'failed';
    } finally {
      this.inFlight.delete(jobId);
    }
  }

  /** True if a device matching (vendorId, productId[, serial]) is attached here. */
  protected async usbHasDevice(vendorId: number, productId: number, serial?: string): Promise<boolean> {
    const res = await UsbPrintRelay.listDevices();
    const devices = res?.devices || [];
    return devices.some(d =>
      d.vendorId === vendorId && d.productId === productId &&
      (!serial || !d.serialNumber || d.serialNumber === serial));
  }

  /** Native USB write. Isolated for testability, mirroring printViaNative. */
  protected async printViaUsb(
    vendorId: number, productId: number, dataBase64: string,
    mode: 'usb' | 'serial', baud?: number, serial?: string,
  ): Promise<void> {
    await UsbPrintRelay.printRaw({ vendorId, productId, dataBase64, mode, baud, serial });
  }

  /**
   * Native socket write. Isolated so it is trivially stubbable in tests and so
   * the plugin surface stays a single narrow data-only call.
   */
  protected async printViaNative(host: string, port: number, dataBase64: string): Promise<void> {
    await RunnerPrintRelay.printRaw({ host, port, dataBase64 });
  }

  private async hasPrinted(jobId: string): Promise<boolean> {
    const ring = await this.loadPrinted();
    return ring.includes(jobId);
  }

  private async recordPrinted(jobId: string): Promise<void> {
    const ring = await this.loadPrinted();
    if (ring.includes(jobId)) { return; }
    ring.push(jobId);
    // Bound the ring: drop the oldest ids past the cap.
    while (ring.length > RunnerPrintRelayService.PRINTED_MAX) { ring.shift(); }
    this.printedCache = ring;
    try {
      await Preferences.set({
        key: RunnerPrintRelayService.PRINTED_PREF_KEY,
        value: JSON.stringify(ring),
      });
    } catch { /* persistence best-effort; in-memory cache still dedups this run */ }
  }

  private async loadPrinted(): Promise<string[]> {
    if (this.printedCache) { return this.printedCache; }
    try {
      const pref = await Preferences.get({ key: RunnerPrintRelayService.PRINTED_PREF_KEY });
      const parsed = pref?.value ? JSON.parse(pref.value) : [];
      this.printedCache = Array.isArray(parsed) ? parsed.filter(x => typeof x === 'string') : [];
    } catch {
      this.printedCache = [];
    }
    return this.printedCache;
  }
}
